// The device stand-in for the discount/* endpoints (staging only, see
// DISCOUNTS_STAND_IN). Codes, their uses and stylists' choices are kept in
// IndexedDB, which signing out doesn't clear (sign-out clears localStorage),
// so a code made as the salon can be used on the booking page as a guest on
// the same device. Falls back to memory where IndexedDB isn't there (tests,
// private windows). Nothing here ever reaches another device.
import { codeKey } from './codes';

const DB = 'beu-discounts-standin';
const VERSION = 1;
const STORES = ['codes', 'uses', 'choices'];

const memory = { codes: new Map(), uses: new Map(), choices: new Map() };
let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined' || !indexedDB) { resolve(null); return; }
      const req = indexedDB.open(DB, VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        STORES.forEach((name) => { if (!db.objectStoreNames.contains(name)) db.createObjectStore(name, { keyPath: 'id' }); });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
  return dbPromise;
};

const all = async (store) => {
  const db = await openDb();
  if (!db) return [...memory[store].values()];
  return new Promise((resolve) => {
    try {
      const req = db.transaction(store, 'readonly').objectStore(store).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([...memory[store].values()]);
    } catch (e) {
      resolve([...memory[store].values()]);
    }
  });
};

const put = async (store, value) => {
  memory[store].set(value.id, value);
  const db = await openDb();
  if (!db) return value;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).put(value);
      tx.oncomplete = () => resolve(value);
      tx.onerror = () => resolve(value);
    } catch (e) {
      resolve(value);
    }
  });
};

const remove = async (store, id) => {
  memory[store].delete(id);
  const db = await openDb();
  if (!db) return;
  await new Promise((resolve) => {
    try {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch (e) {
      resolve();
    }
  });
};

// The figures for a code, from its uses
const statsFrom = (uses) => ({
  uses: uses.length,
  revenue: Math.round(uses.reduce((a, u) => a + (Number(u.total) || 0), 0) * 100) / 100,
  discountGiven: Math.round(uses.reduce((a, u) => a + (Number(u.amount) || 0), 0) * 100) / 100,
  newClients: uses.filter((u) => u.isNew).length,
});

const withStats = (code, uses) => ({ ...code, stats: statsFrom(uses.filter((u) => u.codeId === code.id)) });

export const standIn = {
  // Every code at a location, salon's and stylists', with their figures
  async list(salonId) {
    const [codes, uses] = await Promise.all([all('codes'), all('uses')]);
    return codes.filter((c) => String(c.salonId) === String(salonId)).map((c) => withStats(c, uses));
  },

  async save(code) {
    const id = code.id || `dc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    const { stats, ...rest } = code; // eslint-disable-line no-unused-vars
    const saved = await put('codes', { ...rest, id });
    const uses = await all('uses');
    return withStats(saved, uses);
  },

  async get(id) {
    const [codes, uses] = await Promise.all([all('codes'), all('uses')]);
    const c = codes.find((x) => x.id === id);
    return c ? withStats(c, uses) : null;
  },

  // The code a client typed, at the location they're booking
  async findByCode(salonId, typed) {
    const k = codeKey(typed);
    const list = await this.list(salonId);
    const matches = list.filter((c) => codeKey(c.code) === k);
    // A code can be made again after it ended: the live one wins
    return matches.sort((a, b) => (a.status === 'ended') - (b.status === 'ended') || String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0] || null;
  },

  // How many times this client has used the code (any code with the same
  // letters at this location, so making it again doesn't reset who's used it)
  async usedBy(salonId, typed, clientKey) {
    if (!clientKey) return 0;
    const k = codeKey(typed);
    const uses = await all('uses');
    return uses.filter((u) => String(u.salonId) === String(salonId) && u.codeKey === k && u.clientKey === clientKey).length;
  },

  async redeem(use) {
    const id = `${use.codeId}:${use.bookingId || Date.now()}`;
    return put('uses', { ...use, id, codeKey: codeKey(use.code), at: use.at || new Date().toISOString() });
  },

  // A booking that never went ahead gives its use back
  async release(bookingId) {
    const uses = await all('uses');
    await Promise.all(uses.filter((u) => String(u.bookingId) === String(bookingId)).map((u) => remove('uses', u.id)));
  },

  async useFor(bookingId) {
    const uses = await all('uses');
    return uses.find((u) => String(u.bookingId) === String(bookingId)) || null;
  },

  // A self-employed stylist's answer on salon codes:
  //   { id: `${salonId}:${stylistId}`, salonId, stylistId, salonPays, shared, at }
  async choices(salonId) {
    const list = await all('choices');
    return list.filter((c) => String(c.salonId) === String(salonId));
  },

  async setChoice(salonId, stylistId, answer) {
    return put('choices', { id: `${salonId}:${stylistId}`, salonId: String(salonId), stylistId: String(stylistId), ...answer, at: new Date().toISOString() });
  },

  // Tests only
  async _reset() {
    STORES.forEach((s) => memory[s].clear());
    const db = await openDb();
    if (!db) return;
    await Promise.all(STORES.map((s) => new Promise((resolve) => {
      try {
        const tx = db.transaction(s, 'readwrite');
        tx.objectStore(s).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch (e) { resolve(); }
    })));
  },
};

export default standIn;
